import * as Menu from '@radix-ui/react-dropdown-menu'
import { Check, ChevronRight, Circle } from 'lucide-react'
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type HTMLAttributes } from 'react'

import { cn } from './utils'

export const DropdownMenu = Menu.Root
export const DropdownMenuTrigger = Menu.Trigger
export const DropdownMenuGroup = Menu.Group
export const DropdownMenuPortal = Menu.Portal
export const DropdownMenuSub = Menu.Sub
export const DropdownMenuRadioGroup = Menu.RadioGroup

const itemBase =
  'relative flex cursor-default select-none items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-ink outline-none min-h-10 ' +
  'data-[highlighted]:bg-paper-2 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0'

export const DropdownMenuContent = forwardRef<ElementRef<typeof Menu.Content>, ComponentPropsWithoutRef<typeof Menu.Content>>(
  function DropdownMenuContent({ className, sideOffset = 6, ...props }, ref) {
    return (
      <Menu.Portal>
        <Menu.Content
          ref={ref}
          sideOffset={sideOffset}
          collisionPadding={8}
          className={cn('z-50 min-w-44 card p-1.5 shadow-pop', className)}
          {...props}
        />
      </Menu.Portal>
    )
  },
)

export const DropdownMenuSubTrigger = forwardRef<ElementRef<typeof Menu.SubTrigger>, ComponentPropsWithoutRef<typeof Menu.SubTrigger>>(
  function DropdownMenuSubTrigger({ className, children, ...props }, ref) {
    return (
      <Menu.SubTrigger ref={ref} className={cn(itemBase, 'data-[state=open]:bg-paper-2', className)} {...props}>
        {children}
        <ChevronRight className="ml-auto" aria-hidden />
      </Menu.SubTrigger>
    )
  },
)

export const DropdownMenuSubContent = forwardRef<ElementRef<typeof Menu.SubContent>, ComponentPropsWithoutRef<typeof Menu.SubContent>>(
  function DropdownMenuSubContent({ className, ...props }, ref) {
    return <Menu.SubContent ref={ref} className={cn('z-50 min-w-40 card p-1.5 shadow-pop', className)} {...props} />
  },
)

export const DropdownMenuItem = forwardRef<ElementRef<typeof Menu.Item>, ComponentPropsWithoutRef<typeof Menu.Item> & { inset?: boolean; destructive?: boolean }>(
  function DropdownMenuItem({ className, inset, destructive, ...props }, ref) {
    return <Menu.Item ref={ref} className={cn(itemBase, inset && 'pl-8', destructive && 'text-danger', className)} {...props} />
  },
)

export const DropdownMenuCheckboxItem = forwardRef<ElementRef<typeof Menu.CheckboxItem>, ComponentPropsWithoutRef<typeof Menu.CheckboxItem>>(
  function DropdownMenuCheckboxItem({ className, children, checked, ...props }, ref) {
    return (
      <Menu.CheckboxItem ref={ref} className={cn(itemBase, 'pl-8', className)} checked={checked} {...props}>
        <span className="absolute left-2 flex size-4 items-center justify-center">
          <Menu.ItemIndicator>
            <Check aria-hidden />
          </Menu.ItemIndicator>
        </span>
        {children}
      </Menu.CheckboxItem>
    )
  },
)

export const DropdownMenuRadioItem = forwardRef<ElementRef<typeof Menu.RadioItem>, ComponentPropsWithoutRef<typeof Menu.RadioItem>>(
  function DropdownMenuRadioItem({ className, children, ...props }, ref) {
    return (
      <Menu.RadioItem ref={ref} className={cn(itemBase, 'pl-8', className)} {...props}>
        <span className="absolute left-2 flex size-4 items-center justify-center">
          <Menu.ItemIndicator>
            <Circle className="size-2 fill-current" aria-hidden />
          </Menu.ItemIndicator>
        </span>
        {children}
      </Menu.RadioItem>
    )
  },
)

export const DropdownMenuLabel = forwardRef<ElementRef<typeof Menu.Label>, ComponentPropsWithoutRef<typeof Menu.Label> & { inset?: boolean }>(
  function DropdownMenuLabel({ className, inset, ...props }, ref) {
    return <Menu.Label ref={ref} className={cn('px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink-3', inset && 'pl-8', className)} {...props} />
  },
)

export const DropdownMenuSeparator = forwardRef<ElementRef<typeof Menu.Separator>, ComponentPropsWithoutRef<typeof Menu.Separator>>(
  function DropdownMenuSeparator({ className, ...props }, ref) {
    return <Menu.Separator ref={ref} className={cn('-mx-1.5 my-1.5 h-px bg-line', className)} {...props} />
  },
)

export function DropdownMenuShortcut({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn('ml-auto text-xs tracking-widest text-ink-3', className)} {...props} />
}
